import { Fragment, useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import {
    XMarkIcon,
    UserIcon,
    EnvelopeIcon,
    PhoneIcon,
    IdentificationIcon,
    PencilSquareIcon,
    UserPlusIcon,
} from '@heroicons/react/24/outline'
import { formatCPFOrCNPJ, formatPhone } from '../../../Common'

const getInitials = (nome) => {
    if (!nome) return '?'
    return nome.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase()
}

const ModalInvestidor = ({ states, setters, actionCreate, actionEdit }) => {

    const [nome, setNome] = useState('')
    const [email, setEmail] = useState('')
    const [cpfCnpj, setCpfCnpj] = useState('')
    const [telefone, setTelefone] = useState('')
    const [errors, setErrors] = useState({})

    const clearError = (field) => setErrors(prev => ({ ...prev, [field]: null }))

    const validate = () => {
        const e = {}
        if (!nome.trim()) e.nome = 'Campo obrigatório'
        if (!email.trim()) e.email = 'Campo obrigatório'
        else if (!/^[a-z0-9.]+@[a-z0-9]+\.[a-z]+(\.[a-z]+)?$/i.test(email)) e.email = 'E-mail inválido'
        if (!cpfCnpj.trim()) e.cpfCnpj = 'Campo obrigatório'
        if (!telefone.trim()) e.telefone = 'Campo obrigatório'
        setErrors(e)
        return Object.keys(e).length === 0
    }

    const handleSubmit = () => {
        if (!validate()) return
        const payload = { nome, email, cpf_cnpj: cpfCnpj, telefone }
        if (states.isEdit) actionEdit(states.Investidores?.id, payload)
        else actionCreate(payload)
    }

    useEffect(() => {
        if (!states.modalInvestidores) {
            setNome(''); setEmail(''); setCpfCnpj(''); setTelefone(''); setErrors({})
        } else if (states.isEdit && states.Investidores) {
            const inv = states.Investidores
            setNome(inv.nome || '')
            setEmail(inv.email || '')
            setCpfCnpj(inv.cpf_cnpj || '')
            setTelefone(inv.telefone || '')
        }
    }, [states.modalInvestidores])

    const isEdit = states.isEdit

    return (
        <Transition.Root show={states.modalInvestidores} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setters.setModalInvestidores(false)}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
                    leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all">

                                {/* ── Header ── */}
                                <div className="relative bg-gray-900 px-6 pt-6 pb-14 overflow-hidden">
                                    {/* Detalhe decorativo */}
                                    <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-brand-yellow/10" />
                                    <div className="absolute -bottom-12 -left-6 w-32 h-32 rounded-full bg-brand-yellow/5" />

                                    <div className="relative flex items-start justify-between">
                                        <div className="flex items-center gap-4">
                                            {/* Avatar / ícone */}
                                            <div className="w-14 h-14 rounded-2xl bg-brand-yellow flex items-center justify-center flex-shrink-0 shadow-lg">
                                                {isEdit && nome
                                                    ? <span className="text-xl font-black text-gray-900">{getInitials(nome)}</span>
                                                    : <UserPlusIcon className="w-7 h-7 text-gray-900" />}
                                            </div>
                                            <div>
                                                <span className={`inline-block mb-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest ${isEdit ? 'bg-amber-400/20 text-amber-300' : 'bg-green-400/20 text-green-300'
                                                    }`}>
                                                    {isEdit ? 'Modo edição' : 'Novo cadastro'}
                                                </span>
                                                <Dialog.Title as="h3" className="text-xl font-bold text-white leading-tight">
                                                    {isEdit ? 'Editar Investidor' : 'Novo Investidor'}
                                                </Dialog.Title>
                                                <p className="text-sm text-gray-400 mt-0.5">
                                                    {isEdit
                                                        ? `Atualizando dados de ${states.Investidores?.nome?.split(' ')[0]}`
                                                        : 'Preencha os dados para cadastrar'}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setters.setModalInvestidores(false)}
                                            className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-colors"
                                        >
                                            <XMarkIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>

                                {/* ── Conector header → form ── */}
                                <div className="relative -mt-6 mx-6 z-10">
                                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 space-y-4">

                                        {/* Nome + E-mail lado a lado quando possível */}
                                        <div className="grid grid-cols-1 gap-4">
                                            <Field
                                                id="nome" label="Nome completo" icon={UserIcon}
                                                error={errors.nome}
                                            >
                                                <input
                                                    id="nome" type="text" value={nome}
                                                    onChange={e => { setNome(e.target.value); clearError('nome') }}
                                                    placeholder="Nome completo do investidor"
                                                    className={inputCls(!!errors.nome)}
                                                />
                                            </Field>

                                            <Field
                                                id="inv-email" label="E-mail" icon={EnvelopeIcon}
                                                error={errors.email}
                                            >
                                                <input
                                                    id="inv-email" type="email" value={email}
                                                    onChange={e => { setEmail(e.target.value); clearError('email') }}
                                                    placeholder="email@exemplo.com"
                                                    className={inputCls(!!errors.email)}
                                                />
                                            </Field>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <Field
                                                id="cpfCnpj" label="CPF / CNPJ" icon={IdentificationIcon}
                                                error={errors.cpfCnpj}
                                            >
                                                <input
                                                    id="cpfCnpj" type="text" value={cpfCnpj}
                                                    onChange={e => { setCpfCnpj(formatCPFOrCNPJ(e.target.value)); clearError('cpfCnpj') }}
                                                    placeholder="000.000.000-00"
                                                    maxLength={18}
                                                    className={inputCls(!!errors.cpfCnpj)}
                                                />
                                            </Field>

                                            <Field
                                                id="inv-telefone" label="Telefone" icon={PhoneIcon}
                                                error={errors.telefone}
                                            >
                                                <input
                                                    id="inv-telefone" type="tel" value={telefone}
                                                    onChange={e => { setTelefone(formatPhone(e.target.value)); clearError('telefone') }}
                                                    placeholder="(00) 00000-0000"
                                                    className={inputCls(!!errors.telefone)}
                                                />
                                            </Field>
                                        </div>
                                    </div>
                                </div>

                                {/* ── Footer ── */}
                                <div className="flex items-center justify-end gap-3 px-6 pt-4 pb-6">
                                    <button
                                        type="button"
                                        onClick={() => setters.setModalInvestidores(false)}
                                        className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-gray-900 bg-brand-yellow hover:bg-brand-yellow-dark shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105"
                                    >
                                        {isEdit
                                            ? <><PencilSquareIcon className="w-4 h-4" /> Salvar Alterações</>
                                            : <><UserPlusIcon className="w-4 h-4" /> Cadastrar Investidor</>}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

const inputCls = (hasError) =>
    `mt-1.5 block w-full px-4 py-2.5 rounded-xl border text-sm transition-all duration-200 focus:outline-none focus:ring-2 ${hasError
        ? 'border-red-300 bg-red-50/50 focus:ring-red-400 focus:border-red-400'
        : 'border-gray-200 bg-white focus:ring-brand-yellow focus:border-brand-yellow'
    }`

const Field = ({ id, label, icon: Icon, error, children }) => (
    <div>
        <label htmlFor={id} className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
            <Icon className="w-3.5 h-3.5" />
            {label}
        </label>
        {children}
        {error && (
            <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                <span>⚠</span> {error}
            </p>
        )}
    </div>
)

export default ModalInvestidor
